import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

import { DollarFormatter, PercentageFormatter } from '@src/components';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  METRIC_COLUMNS,
  safeNumber,
  formatShortDate,
  formatBackendFlag,
  formatWhyDriverChip,
  getStatusPillStyles,
  getRatioTone,
  getPriorityTone,
  getBuildTone,
  getConfidenceTone,
  getRecommendationTone,
  getActiveTone,
  getVerdictTone,
  getStageTone,
  getWhyDriverTone,
  getComConfidenceTone,
  getFormatTone,
  getExpectedMatureEndDate,
} from './utils';

// ─── Renderers ──────────────────────────────────────────────────────────────

const renderMetricValue = (value, type) => {
  if (type === 'currency') return <DollarFormatter value={value} />;
  if (type === 'ratio') return <PercentageFormatter value={value} />;
  return safeNumber(value).toLocaleString();
};

const renderStatusPill = (label, tone = 'neutral') => {
  const colors = getStatusPillStyles(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.1,
        py: 0.45,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {label || '-'}
    </Box>
  );
};

const renderDimensionValue = (column, row, onPreviewOpen, qaControls = {}) => {
  if (column.key === 'previewAction') {
    const { onToggleQa, qaExpanded = false, qaLoading = false } = qaControls;
    const canPreview = row.thumbnailUrl || row.imageUrl || row.headline || row.primaryText;
    const canQa = row.clientId && row.adId;
    if (!canPreview && !canQa) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }
    return (
      <Stack direction="row" spacing={1}>
        {canPreview ? (
          <Button size="small" variant="outlined" onClick={() => onPreviewOpen(row)}>
            Preview
          </Button>
        ) : null}
        {canQa ? (
          <Button size="small" variant="text" onClick={() => onToggleQa?.(row)}>
            {qaLoading ? 'Loading\u2026' : qaExpanded ? 'Hide QA' : 'QA'}
          </Button>
        ) : null}
      </Stack>
    );
  }

  if (column.key === 'activeStatusLabel') return renderStatusPill(row[column.key] || '-', getActiveTone(row.isActive));
  if (column.key === 'facebookFormat') return renderStatusPill(row[column.key] || 'Unknown', getFormatTone(row[column.key]));
  if (column.key === 'stageLabel') return renderStatusPill(row[column.key] || '-', getStageTone(row[column.key]));
  if (column.key === 'confidenceLabel') return renderStatusPill(row[column.key] || '-', getConfidenceTone(row[column.key]));
  if (column.key === 'comConfidenceLabel') return renderStatusPill(row[column.key] || '-', getComConfidenceTone(row[column.key]));
  if (column.key === 'spendShareLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.spendShare));
  if (column.key === 'primaryKpiLabel') return renderStatusPill(row[column.key] || '-', 'neutral');
  if (column.key === 'predictedCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.predictedCostOfMarketing));
  if (column.key === 'effectiveCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.effectiveCostOfMarketing));
  if (column.key === 'pausePriorityLabel') return renderStatusPill(row[column.key] || '-', getPriorityTone(row.pausePriority));
  if (column.key === 'buildPriorityLabel') return renderStatusPill(row[column.key] || '-', getBuildTone(row.buildPriority));
  if (column.key === 'creativeVerdict') return renderStatusPill(row[column.key] || '-', getVerdictTone(row[column.key]));
  if (column.key === 'overallRecommendation') return renderStatusPill(row[column.key] || '-', getRecommendationTone(row[column.key]));

  if (column.key === 'whyLabel') {
    const whyDriverChips = Array.isArray(row.whyDriverChips) ? row.whyDriverChips : [];
    return (
      <Stack spacing={0.6} sx={{ maxWidth: column.minWidth }}>
        <Typography noWrap sx={{ maxWidth: column.minWidth, fontSize: column.fontSize || 'inherit', lineHeight: 1.25 }}>
          {row[column.key] || '-'}
        </Typography>
        {whyDriverChips.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {whyDriverChips.map((driver) => (
              <React.Fragment key={`${driver.key}-${driver.label}`}>
                {renderStatusPill(formatWhyDriverChip(driver), getWhyDriverTone(driver.directionalDelta))}
              </React.Fragment>
            ))}
          </Box>
        ) : null}
      </Stack>
    );
  }

  if (column.key === 'adName') {
    const secondaryLabel = row.adId
      ? `Ad ID: ${row.adId}`
      : [row.themeName, row.subthemeName].filter(Boolean).join(' \u2022 ');
    return (
      <Stack spacing={0.25} sx={{ maxWidth: column.minWidth }}>
        <Typography noWrap sx={{ maxWidth: column.minWidth, fontSize: column.fontSize || 'inherit', fontWeight: 600, lineHeight: 1.25 }}>
          {row.adName || '-'}
        </Typography>
        {secondaryLabel ? (
          <Typography noWrap variant="caption" color="text.secondary" sx={{ maxWidth: column.minWidth, lineHeight: 1.2 }}>
            {secondaryLabel}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  if (column.type === 'count') return safeNumber(row[column.key]).toLocaleString();
  if (column.truncate) {
    return (
      <Typography noWrap sx={{ maxWidth: column.minWidth, fontSize: column.fontSize || 'inherit', lineHeight: 1.25 }}>
        {row[column.key] || '-'}
      </Typography>
    );
  }
  return row[column.key] || '-';
};

// ─── Column visibility popover ──────────────────────────────────────────────

function ColumnVisibilityControl({ visibleColumns, onToggle }) {
  const [anchorEl, setAnchorEl] = React.useState(null);

  return (
    <>
      <Tooltip title="Toggle column visibility">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxHeight: 400, overflowY: 'auto', minWidth: 220 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Metric Columns</Typography>
          {METRIC_COLUMNS.map((col) => (
            <FormControlLabel
              key={col.key}
              sx={{ display: 'block', ml: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={visibleColumns.has(col.key)}
                  onChange={() => onToggle(col.key)}
                />
              }
              label={<Typography variant="body2">{col.label}</Typography>}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
}

// ─── QA Panel ───────────────────────────────────────────────────────────────

function QaPanel({ row, qaDetail, qaLoading, qaError, startDate, endDate }) {
  const expectedMatureEndDate = getExpectedMatureEndDate(startDate, endDate);
  const expectedMatureEndDateLabel = formatShortDate(expectedMatureEndDate);
  const rowMatureEndDateLabel = formatShortDate(row.matureEndDate);
  const hasCutoffMismatch = expectedMatureEndDateLabel && rowMatureEndDateLabel && expectedMatureEndDateLabel !== rowMatureEndDateLabel;

  return (
    <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack spacing={2}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>QA Snapshot</Typography>
          <Typography variant="body2" color="text.secondary">
            Full window uses {formatDateToYMD(startDate)} - {formatDateToYMD(endDate)}. Expected backend maturity cutoff: {expectedMatureEndDateLabel || 'Not eligible'}.
          </Typography>
          {hasCutoffMismatch ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Row-provided maturity cutoff is {rowMatureEndDateLabel}, which does not match the current range cutoff of {expectedMatureEndDateLabel}.
            </Alert>
          ) : null}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(220px, 280px))' }, gap: 2, alignItems: 'start', justifyContent: 'start' }}>
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: '100%' }}>
            <Typography variant="overline" color="text.secondary">Full Window</Typography>
            <Typography variant="body2">Spend: {renderMetricValue(row.spend, 'currency')}</Typography>
            <Typography variant="body2">Leads: {renderMetricValue(row.leads, 'count')}</Typography>
            <Typography variant="body2">Self Books: {renderMetricValue(row.selfBook, 'count')}</Typography>
            <Typography variant="body2">Booked: {renderMetricValue(row.booked, 'count')}</Typography>
            <Typography variant="body2">Scheduled: {renderMetricValue(row.scheduled, 'count')}</Typography>
            <Typography variant="body2">Issues: {renderMetricValue(row.issues, 'count')}</Typography>
            <Typography variant="body2">Demos: {renderMetricValue(row.demos, 'count')}</Typography>
            <Typography variant="body2">Net Sold: {renderMetricValue(row.netSold, 'count')}</Typography>
            <Typography variant="body2">Net Amount: {renderMetricValue(row.netAmount, 'currency')}</Typography>
          </Box>
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: '100%' }}>
            <Typography variant="overline" color="text.secondary">Mature Cohorts</Typography>
            <Typography variant="body2">Mature Through: {rowMatureEndDateLabel || expectedMatureEndDateLabel || '-'}</Typography>
            <Typography variant="body2">Spend: {renderMetricValue(row.matureSpend, 'currency')}</Typography>
            <Typography variant="body2">Leads: {renderMetricValue(row.matureLeads, 'count')}</Typography>
            <Typography variant="body2">Self Books: {renderMetricValue(row.matureSelfBook, 'count')}</Typography>
            <Typography variant="body2">Booked: {renderMetricValue(row.matureBooked, 'count')}</Typography>
            <Typography variant="body2">Scheduled: {renderMetricValue(row.matureScheduled, 'count')}</Typography>
            <Typography variant="body2">Issues: {renderMetricValue(row.matureIssues, 'count')}</Typography>
            <Typography variant="body2">Demos: {renderMetricValue(row.matureDemos, 'count')}</Typography>
            <Typography variant="body2">Net Sold: {renderMetricValue(row.matureNetSold, 'count')}</Typography>
            <Typography variant="body2">Net Amount: {renderMetricValue(row.matureNetAmount, 'currency')}</Typography>
          </Box>
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, width: '100%' }}>
            <Typography variant="overline" color="text.secondary">Scoring Context</Typography>
            <Typography variant="body2">Stage: {row.stageLabel || '-'}</Typography>
            <Typography variant="body2">Primary KPI: {row.primaryKpiLabel || '-'}</Typography>
            <Typography variant="body2">Confidence: {row.confidenceLabel || '-'}</Typography>
            <Typography variant="body2">CoM Confidence: {row.comConfidenceLabel || '-'}</Typography>
            <Typography variant="body2">Recommendation: {row.overallRecommendation || '-'}</Typography>
            <Typography variant="body2">Format: {row.facebookFormat || '-'}</Typography>
            <Typography variant="body2">Has Backend Coverage: {formatBackendFlag(row.hasBackendIntegration)}</Typography>
          </Box>
        </Box>
        {qaError ? <Alert severity="error">{qaError}</Alert> : null}
        {qaLoading ? (
          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} /></Box>
        ) : (
          <Box>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Backend Records</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lead Created</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Appointment</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Demo</TableCell>
                  <TableCell>Sold</TableCell>
                  <TableCell>Gross</TableCell>
                  <TableCell>Net</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(qaDetail?.records || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No backend records found for this ad in the selected lead-cohort window.</TableCell>
                  </TableRow>
                ) : (qaDetail?.records || []).map((record) => (
                  <TableRow key={record.id || record.unique_id}>
                    <TableCell>{formatShortDate(record.lead_created_date) || '-'}</TableCell>
                    <TableCell>{formatShortDate(record.created_date) || '-'}</TableCell>
                    <TableCell>{formatShortDate(record.appointment_date) || '-'}</TableCell>
                    <TableCell>{formatBackendFlag(record.issue)}</TableCell>
                    <TableCell>{formatBackendFlag(record.demo)}</TableCell>
                    <TableCell>{formatShortDate(record.sold_date) || '-'}</TableCell>
                    <TableCell>{renderMetricValue(record.gross_amount, 'currency')}</TableCell>
                    <TableCell>{renderMetricValue(record.net_amount, 'currency')}</TableCell>
                    <TableCell>{record.status || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {qaDetail?.totalCount > (qaDetail?.records || []).length ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Showing first {(qaDetail?.records || []).length} of {qaDetail?.totalCount} backend records.
              </Typography>
            ) : null}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

// ─── Data row ───────────────────────────────────────────────────────────────

const DataRow = React.memo(function DataRow({
  row, dimensionColumns, visibleMetricColumns, onPreviewOpen, onToggleQa,
  expandedQaRows, qaRowLoading, qaRowDetails, qaRowErrors, startDate, endDate,
}) {
  const qaExpanded = Boolean(expandedQaRows[row.uuid]);
  const colSpan = dimensionColumns.length + visibleMetricColumns.length;

  return (
    <React.Fragment>
      <TableRow hover sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 48px' }}>
        {dimensionColumns.map((column) => (
          <TableCell
            key={`${row.uuid}-${column.key}`}
            title={column.truncate ? (row[column.key] || '') : undefined}
            sx={column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 2, backgroundColor: 'background.paper' } : undefined}
          >
            {renderDimensionValue(column, row, onPreviewOpen, {
              onToggleQa,
              qaExpanded,
              qaLoading: Boolean(qaRowLoading[row.uuid]),
            })}
          </TableCell>
        ))}
        {visibleMetricColumns.map((column) => (
          <TableCell key={column.key}>{renderMetricValue(row[column.key], column.type)}</TableCell>
        ))}
      </TableRow>
      {qaExpanded ? (
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ p: 0, bgcolor: '#fcfcfd' }}>
            <QaPanel
              row={row}
              qaDetail={qaRowDetails[row.uuid]}
              qaLoading={Boolean(qaRowLoading[row.uuid])}
              qaError={qaRowErrors[row.uuid]}
              startDate={startDate}
              endDate={endDate}
            />
          </TableCell>
        </TableRow>
      ) : null}
    </React.Fragment>
  );
});

// ─── Main table component ───────────────────────────────────────────────────

export default function CreativeTable({
  viewMode,
  decisionRows,
  adHierarchy,
  dimensionColumns,
  totals,
  loading,
  loadingClientMeta,
  startDate,
  endDate,
  onPreviewOpen,
  onToggleQa,
  expandedClients,
  expandedCampaigns,
  expandedAdSets,
  expandedQaRows,
  qaRowDetails,
  qaRowLoading,
  qaRowErrors,
  toggleClientExpanded,
  toggleCampaignExpanded,
  toggleAdSetExpanded,
}) {
  // Fix 5: Column visibility state
  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState(
    () => new Set(METRIC_COLUMNS.map((c) => c.key))
  );
  // Fix 5: Sort state
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDirection, setSortDirection] = React.useState('desc');

  const handleToggleColumn = React.useCallback((key) => {
    setVisibleColumnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const visibleMetricColumns = React.useMemo(
    () => METRIC_COLUMNS.filter((col) => visibleColumnKeys.has(col.key)),
    [visibleColumnKeys]
  );

  const handleSort = React.useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return key;
      }
      setSortDirection('desc');
      return key;
    });
  }, []);

  const sortedRows = React.useMemo(() => {
    if (!sortKey || viewMode === 'ad') return decisionRows;
    const col = METRIC_COLUMNS.find((c) => c.key === sortKey) || dimensionColumns.find((c) => c.key === sortKey);
    if (!col) return decisionRows;
    return [...decisionRows].sort((a, b) => {
      const av = safeNumber(a[sortKey]);
      const bv = safeNumber(b[sortKey]);
      return sortDirection === 'asc' ? av - bv : bv - av;
    });
  }, [decisionRows, dimensionColumns, sortDirection, sortKey, viewMode]);

  const colSpan = dimensionColumns.length + visibleMetricColumns.length;

  if (loading || loadingClientMeta) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1 }}>
        <ColumnVisibilityControl visibleColumns={visibleColumnKeys} onToggle={handleToggleColumn} />
      </Box>
      <TableContainer sx={{ maxHeight: '74vh' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 2400 }}>
          <TableHead>
            <TableRow>
              {dimensionColumns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    minWidth: column.minWidth,
                    ...(column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 3, backgroundColor: 'background.paper' } : {}),
                  }}
                >
                  {column.helpText ? (
                    <Tooltip title={column.helpText} arrow>
                      <Box component="span" sx={{ cursor: 'help' }}>{column.label}</Box>
                    </Tooltip>
                  ) : column.label}
                </TableCell>
              ))}
              {visibleMetricColumns.map((column) => (
                <TableCell key={column.key} sx={{ minWidth: 120 }} sortDirection={sortKey === column.key ? sortDirection : false}>
                  <TableSortLabel
                    active={sortKey === column.key}
                    direction={sortKey === column.key ? sortDirection : 'desc'}
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {decisionRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 6 }}>
                  No rows match the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {viewMode === 'ad' ? adHierarchy.map((clientGroup) => {
                  const clientExpanded = expandedClients[clientGroup.key] ?? true;
                  return (
                    <React.Fragment key={clientGroup.key}>
                      <TableRow sx={{ contentVisibility: 'auto' }}>
                        <TableCell colSpan={colSpan} sx={{ bgcolor: '#f6f8fb', borderBottomColor: 'divider', py: 1.25 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" onClick={() => toggleClientExpanded(clientGroup.key)}>
                              {clientExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                            <Typography sx={{ fontWeight: 800 }}>{clientGroup.clientName}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {clientGroup.verticalName || 'Unassigned Vertical'} &bull; {clientGroup.rows.length} ads &bull; <DollarFormatter value={clientGroup.spend} />
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {clientExpanded ? clientGroup.campaigns.map((campaignGroup) => {
                        const campaignExpanded = expandedCampaigns[campaignGroup.key] ?? true;
                        return (
                          <React.Fragment key={campaignGroup.key}>
                            <TableRow sx={{ contentVisibility: 'auto' }}>
                              <TableCell colSpan={colSpan} sx={{ bgcolor: '#fbfcfe', borderBottomColor: 'divider', py: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3 }}>
                                  <IconButton size="small" onClick={() => toggleCampaignExpanded(campaignGroup.key)}>
                                    {campaignExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                  </IconButton>
                                  <Typography sx={{ fontWeight: 700 }}>{campaignGroup.campaign}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {campaignGroup.rows.length} ads &bull; <DollarFormatter value={campaignGroup.spend} />
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                            {campaignExpanded ? campaignGroup.adSets.map((adSetGroup) => {
                              const adSetExpanded = expandedAdSets[adSetGroup.key] ?? true;
                              return (
                                <React.Fragment key={adSetGroup.key}>
                                  <TableRow sx={{ contentVisibility: 'auto' }}>
                                    <TableCell colSpan={colSpan} sx={{ bgcolor: '#ffffff', borderBottomColor: 'divider', py: 0.9 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 6 }}>
                                        <IconButton size="small" onClick={() => toggleAdSetExpanded(adSetGroup.key)}>
                                          {adSetExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                        </IconButton>
                                        <Typography sx={{ fontWeight: 700 }}>{adSetGroup.adSetName}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {adSetGroup.rows.length} ads &bull; <DollarFormatter value={adSetGroup.spend} />
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  {adSetExpanded ? adSetGroup.rows.map((row) => (
                                    <DataRow
                                      key={row.uuid}
                                      row={row}
                                      dimensionColumns={dimensionColumns}
                                      visibleMetricColumns={visibleMetricColumns}
                                      onPreviewOpen={onPreviewOpen}
                                      onToggleQa={onToggleQa}
                                      expandedQaRows={expandedQaRows}
                                      qaRowLoading={qaRowLoading}
                                      qaRowDetails={qaRowDetails}
                                      qaRowErrors={qaRowErrors}
                                      startDate={startDate}
                                      endDate={endDate}
                                    />
                                  )) : null}
                                </React.Fragment>
                              );
                            }) : null}
                          </React.Fragment>
                        );
                      }) : null}
                    </React.Fragment>
                  );
                }) : sortedRows.map((row) => (
                  <TableRow key={row.uuid} hover sx={{ contentVisibility: 'auto', containIntrinsicSize: '0 48px' }}>
                    {dimensionColumns.map((column) => (
                      <TableCell
                        key={`${row.uuid}-${column.key}`}
                        title={column.truncate ? (row[column.key] || '') : undefined}
                        sx={column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 2, backgroundColor: 'background.paper' } : undefined}
                      >
                        {renderDimensionValue(column, row, onPreviewOpen)}
                      </TableCell>
                    ))}
                    {visibleMetricColumns.map((column) => (
                      <TableCell key={column.key}>{renderMetricValue(row[column.key], column.type)}</TableCell>
                    ))}
                  </TableRow>
                ))}

                <TableRow>
                  {dimensionColumns.map((column, index) => (
                    <TableCell
                      key={`total-${column.key}`}
                      sx={{
                        fontWeight: 700,
                        ...(column.sticky ? { position: 'sticky', left: column.stickyLeft || 0, zIndex: 2, backgroundColor: 'background.paper' } : {}),
                      }}
                    >
                      {index === 0 ? 'Total' : '-'}
                    </TableCell>
                  ))}
                  {visibleMetricColumns.map((column) => (
                    <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                      {renderMetricValue(totals[column.key], column.type)}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
